import Vue from "vue";
import ApiService from "../../services/ApiService";
import { EventBus, EVENTS } from "../../services/EventBus";

Vue.component("transfer-check-widget", {
  delimiters: ["${", "}"],

  props: {
    transferId: {
      type: Number,
      required: true,
    },
    transferName: {
      type: String,
      default: "Transfer",
    },
  },

  data() {
    return {
      currentStep: 0, // 0 = Frage 1, 1 = Frage 2, 2 = Frage 3, 3 = Ergebnis
      isLoading: false,
      isLoggedIn: false,
      hasVoted: false,

      // User-Antworten
      userAnswers: {
        sportlichPassend: null, // "ja" oder "nein"
        preisEinschaetzung: null, // "zu_teuer", "moeglich", "schnaeppchen"
        transferArt: null, // "leihe", "fest", "gar_nicht"
      },

      // Ergebnisse von allen Usern
      results: {
        sportlichPassend: { ja: 0, nein: 0 },
        preisEinschaetzung: { zu_teuer: 0, moeglich: 0, schnaeppchen: 0 },
        transferArt: { leihe: 0, fest: 0, gar_nicht: 0 },
        total: 0,
      },

      message: "",
    };
  },

  computed: {
    totalVotes() {
      return this.results.total || 0;
    },

    // Prozentuale Auswertung für Frage 1
    sportlichJaProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.sportlichPassend.ja / this.totalVotes) * 100);
    },
    sportlichNeinProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.sportlichPassend.nein / this.totalVotes) * 100);
    },

    // Prozentuale Auswertung für Frage 2
    preisZuTeuerProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.preisEinschaetzung.zu_teuer / this.totalVotes) * 100);
    },
    preisMoeglichProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.preisEinschaetzung.moeglich / this.totalVotes) * 100);
    },
    preisSchnaeppchenProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.preisEinschaetzung.schnaeppchen / this.totalVotes) * 100);
    },

    // Prozentuale Auswertung für Frage 3
    transferLeiheProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.transferArt.leihe / this.totalVotes) * 100);
    },
    transferFestProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.transferArt.fest / this.totalVotes) * 100);
    },
    transferGarNichtProzent() {
      if (this.totalVotes === 0) return 0;
      return Math.round((this.results.transferArt.gar_nicht / this.totalVotes) * 100);
    },

    // Beliebteste Antwort pro Frage
    beliebtsteSportlichAntwort() {
      if (this.results.sportlichPassend.ja >= this.results.sportlichPassend.nein) {
        return "ja";
      }
      return "nein";
    },
    beliebtstePreisAntwort() {
      const max = Math.max(this.results.preisEinschaetzung.zu_teuer, this.results.preisEinschaetzung.moeglich, this.results.preisEinschaetzung.schnaeppchen);
      if (this.results.preisEinschaetzung.zu_teuer === max) return "zu_teuer";
      if (this.results.preisEinschaetzung.moeglich === max) return "moeglich";
      return "schnaeppchen";
    },
    beliebtsteTransferArtAntwort() {
      const max = Math.max(this.results.transferArt.leihe, this.results.transferArt.fest, this.results.transferArt.gar_nicht);
      if (this.results.transferArt.leihe === max) return "leihe";
      if (this.results.transferArt.fest === max) return "fest";
      return "gar_nicht";
    },
  },

  created() {
    // SSR-safe: Set appropriate template based on environment
    if (typeof window !== "undefined") {
      // Browser: Use the full template from DOM
      this.$options.template = "#vue-transfer-check-widget";
    } else {
      // SSR: Render a simple placeholder div
      this.$options.template = '<div class="transfer-check-widget"><div class="text-center p-5"><i class="fa fa-spinner fa-spin"></i></div></div>';
    }
  },

  mounted() {
    // Only load in browser, not during SSR
    if (typeof window !== "undefined") {
      this.loadData();
    }
  },

  methods: {
    loadData() {
      this.isLoading = true;

      ApiService.get("/rest/fanliebling/transfercheck", {
        transferId: this.transferId,
      })
        .done((response) => {
          this.results = response.results || this.results;
          this.hasVoted = response.hasVoted || false;
          this.isLoggedIn = response.isLoggedIn || false;

          // Wenn User bereits gevotet hat, zeige Ergebnis
          if (this.hasVoted && response.userAnswers) {
            this.userAnswers = response.userAnswers;
            this.currentStep = 3; // Direkt zu Ergebnis
          }
        })
        .fail((error) => {
          console.error("Fehler beim Laden der Transfer-Check-Daten:", error);
          this.checkLoginStatus();
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    checkLoginStatus() {
      if (this.$store && this.$store.state && this.$store.state.user) {
        this.isLoggedIn = !!this.$store.state.user.isLoggedIn;
      }
    },

    openLoginModal() {
      // Lade das Modal via Store (wenn noch nicht geladen)
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch('loadComponent', 'login-modal');
      }
      
      // SSR-safe: Only run on client
      if (typeof window === "undefined" || typeof document === "undefined") {
        return;
      }
      // Warte kurz, dann triggere Bootstrap's Modal via DOM-Event
      setTimeout(() => {
        const modalElement = document.getElementById('login');
        if (modalElement && window.$) {
          window.$('#login').modal('show');
        }
      }, 150);
    },

    // Antwort auf Frage 1 - Login-Prüfung
    answerSportlich(antwort) {
      if (!this.isLoggedIn) {
        this.openLoginModal();
        return;
      }
      this.userAnswers.sportlichPassend = antwort;
      this.nextStep();
    },

    // Antwort auf Frage 2
    answerPreis(antwort) {
      this.userAnswers.preisEinschaetzung = antwort;
      this.nextStep();
    },

    // Antwort auf Frage 3
    answerTransferArt(antwort) {
      this.userAnswers.transferArt = antwort;
      this.submitVote();
    },

    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    submitVote() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.message = "";

      ApiService.post("/rest/fanliebling/transfercheck/vote", {
        transferId: this.transferId,
        answers: this.userAnswers,
      })
        .done((response) => {
          console.log("Transfer-Check Vote Response:", response);

          if (response.success) {
            this.results = response.results || this.results;
            this.hasVoted = true;
            this.currentStep = 3; // Zeige Ergebnis
            this.message = response.message || "Vielen Dank für deine Einschätzung!";
            
            // Event auslösen für Badge-Update
            EventBus.$emit(EVENTS.INTERACTION_COMPLETED);
          } else {
            this.message = response.message || "Ein Fehler ist aufgetreten.";
          }
        })
        .fail((error) => {
          console.error("Transfer-Check Vote Error:", error);
          if (error.responseJSON && error.responseJSON.message) {
            this.message = error.responseJSON.message;
          } else {
            this.message = "Sie müssen eingeloggt sein, um teilzunehmen.";
          }
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    restart() {
      this.currentStep = 0;
      this.userAnswers = {
        sportlichPassend: null,
        preisEinschaetzung: null,
        transferArt: null,
      };
    },
  },
});
